import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal"
];

const AddressModal = ({ show, onHide, onSubmit, address }) => {
  const locationData = useSelector((state) => state.location.locationData);

  // Add validation patterns
  const validationPatterns = {
    buildingName: /^[a-zA-Z0-9\s\-.,#]{2,50}$/,
    street: /^[a-zA-Z0-9\s\-.,#]{5,100}$/,
    landmark: /^[a-zA-Z0-9\s\-.,#]{0,50}$/,
    city: /^[a-zA-Z\s]{2,30}$/,
    postalCode: /^\d{6}$/,
    country: /^[a-zA-Z\s]{2,30}$/,
  };

  // Add error state
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    type: "Residential",
    buildingName: "",
    street: "",
    landmark: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  useEffect(() => {
    if (address) {
      setFormData(address);
    } else if (locationData && !address) {
      // Only auto-populate if creating new address and location data exists
      setFormData({
        type: "Residential",
        buildingName: "",
        street: "",
        landmark: "",
        city: locationData.city || "",
        state: locationData.state || "",
        postalCode: locationData.postalCode || "",
        country: locationData.country || "",
      });
    }
  }, [address, locationData]);

  const validateField = (name, value) => {
    if (name === 'state') return true; // State is handled by dropdown
    if (!validationPatterns[name]) return true;
    return validationPatterns[name].test(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const isValid = validateField(name, value);

    setErrors(prev => ({
      ...prev,
      [name]: !isValid
    }));

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (name === "postalCode" && value.length === 6) {
      fetchAddressFromPincode(value);
    }
  };

  const fetchAddressFromPincode = async (pincode) => {
    try {
      const response = await fetch(`https://api.zippopotam.us/IN/${pincode}`);
      // console.log(" response address from pincode", response);
      const data = await response.json();

      if (data && data.places && data.places.length > 0) {
        setFormData((prev) => ({
          ...prev,
          city: data.places[0]["place name"] || prev.city,
          state: data.places[0]["state"] || prev.state,
          country: data.country || prev.country,
        }));
      } else {
        return;
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      type: "Residential",
      buildingName: "",
      street: "",
      landmark: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    });
  };

  if (!show) return null;

  return (
    <div className="modal-overlay container">
      <div className="modal-wrapper">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="section-title">
              {address ? "Edit Address" : "Add New Address"}
            </h4>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="profile-innr-frm">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>Address Type</label>
                      <span className="text-danger"> *</span>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="Residential">Residential</option>
                        <option value="Office">Office</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>Building Name</label>
                      <span className="text-danger"> *</span>
                      <input
                        type="text"
                        name="buildingName"
                        value={formData.buildingName}
                        onChange={handleChange}
                        className={`form-control ${errors.buildingName ? 'is-invalid' : ''}`}
                        required
                        maxLength={50}
                      />
                      {errors.buildingName && (
                        <div className="invalid-feedback">
                          Please enter a valid building name (2-50 characters, alphanumeric)
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="custom-frm-bx">
                      <label>Street</label>
                      <span className="text-danger"> *</span>
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleChange}
                        className="form-control"
                        required
                        maxLength={50}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="custom-frm-bx">
                      <label>Landmark</label>
                      <input
                        type="text"
                        name="landmark"
                        value={formData.landmark}
                        onChange={handleChange}
                        className="form-control"
                        maxLength={50}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>Postal Code</label>
                      <span className="text-danger"> *</span>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,6}$/.test(value)) {
                            handleChange(e);
                          }
                        }}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>City</label>
                      <span className="text-danger"> *</span>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="form-control"
                        required
                        maxLength={30}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>State</label>
                      <span className="text-danger"> *</span>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="form-select"
                        maxLength={20}
                        required
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="custom-frm-bx">
                      <label>Country</label>
                      <span className="text-danger"> *</span>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="form-select"
                        maxLength={20}
                        required
                      >
                        <option selected value="India">
                          India
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="col-lg-12 text-center">
                    <button
                      type="button"
                      className="thm-btn dark me-2"
                      onClick={onHide}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="thm-btn">
                      {address ? "Update" : "Add"} Address
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;
